import { Column, Cell, KeyValuePairType, SortState, Row } from '../types';
import { range, sortCopy } from './arrayUtils';

type ColValueType = 'string' | 'number' | 'date';

type RowValueType = string | number | boolean | Date;

export const createColumn = (
  id: string,
  text: string,
  valueType: ColValueType,
  width: string,
  sortable: boolean,
): Column => (
  {
    id,
    text,
    valueType,
    width,
    sortable,
  }
);

export const createCell = (
  columnId: string,
  value: KeyValuePairType,
  render?: React.ReactNode,
): Cell => (
  {
    columnId,
    value,
    render,
  }
);

export const hasSearchString = (stringList: string[], searchString: string): boolean => {
  if (searchString.startsWith('-')) {
    if (searchString.length <= 1) {
      return true;
    }
    const negationString = searchString.slice(1);
    const regExNegated = new RegExp(negationString.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&'));
    return stringList.reduce((acc: boolean, curr) => (
      acc && !curr.startsWith(negationString) && !regExNegated.test(curr)
    ), true);
  }
  const regEx = new RegExp(searchString.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&'));
  return stringList.reduce((acc: boolean, curr) => (
    acc || curr.startsWith(searchString) || regEx.test(curr)
  ), false);
};

export const calculateNewSortState = (
  columnId: string,
  currentSortOrder: SortState,
): SortState => {
  if (currentSortOrder.columnId === columnId) {
    return {
      ...currentSortOrder,
      direction: currentSortOrder.direction === 'asc' ? 'desc' : 'asc',
    };
  }
  return { columnId, direction: 'asc' };
};

export const calculatePaginationPageDisplay = (currentPage: number, numberOfPages: number) => {
  const pagination = {
    pages: [] as Array<number>,
    hasBreakAtBeginning: false,
    hasBreakAtEnd: false,
  };
  if (numberOfPages < 6) {
    // 1 2 3 4 5
    pagination.pages = range(0, Math.min(numberOfPages || 1, 5));
  } else if (numberOfPages === 6) {
    if (currentPage < 3) {
      // 1 2 3 4 ... 6
      pagination.pages = range(0, 4);
      pagination.hasBreakAtEnd = true;
    } else {
      // 1 ... 3 4 5 6
      pagination.pages = range(2, 6);
      pagination.hasBreakAtBeginning = true;
    }
  } else if (numberOfPages > 6) {
    if (currentPage < 3) {
      // 1 2 3 4 5 ... 7
      pagination.pages = range(0, 5);
      pagination.hasBreakAtEnd = true;
    } else if (currentPage > numberOfPages - 4) {
      // 1 ... 3 4 5 6 7
      pagination.pages = range(numberOfPages - 5, numberOfPages);
      pagination.hasBreakAtBeginning = true;
    } else {
      // 1 ... 3 4 5 ... 7
      pagination.pages = range(currentPage - 1, currentPage + 2);
      pagination.hasBreakAtBeginning = true;
      pagination.hasBreakAtEnd = true;
    }
  }
  return pagination;
};

const validateValueType = (value: KeyValuePairType, valueType: Column['valueType']): void => {
  if (value === undefined || value === null) {
    return;
  }
  if (valueType === 'string' && typeof value !== 'string') {
    throw Error('Non-string value found in column with type "string"')
  }
  if (valueType === 'boolean' && typeof value !== 'boolean') {
    throw Error('Non-boolean value found in column with type "boolean"')
  }
  if (valueType === 'number' && typeof value !== 'number') {
    throw Error('Non-number value found in column with type "number"')
  }
  if (valueType === 'date' && !(value instanceof Date)) {
    throw Error('Non-date value found in column with type "date"')
  }
}

const getValueForComparison = (value: KeyValuePairType, valueType: Column['valueType']): number | string => {
  validateValueType(value, valueType);
  switch (valueType) {
    case 'string':
      return (value ?? '') as string
    case 'number':
      return (value ?? -1) as number
    case 'boolean':
      if (value === true) {
        return 1
      } else if (value === false) {
        return 0
      }
      return -1
    case 'date':
      return value ? (value as Date).getTime() : 0
    default:
      throw Error("Invalid column value type");
  }
}

export const sortRows = (rows: Row[], column: Column, sortOrder: 'asc' | 'desc') => {
  if (!column) {
    return rows;
  }

  const columnId = column.id;
  const columnValueType = column.valueType;
  const compareFn = (a: Row, b: Row) => {
    const aCell= a.cells.find(cell => cell.columnId == columnId);
    const bCell = b.cells.find(cell => cell.columnId == columnId);
    const aValue = getValueForComparison(aCell?.value, columnValueType);
    const bValue = getValueForComparison(bCell?.value, columnValueType);

    let result;
    if (columnValueType === 'string') {
      result = (aValue as string).localeCompare(bValue as string);
    } else { // every other data type is converted to a number
      result = (aValue as number) - (bValue as number);
    }

    return sortOrder === 'asc' ? result : (result * -1);
  }

  return sortCopy(rows, compareFn);
};

export const getCurrentPaginationText = (currentPage: number, itemsPerPage: number, totalCount: number): string => {
  const currentSetMin = Math.min((itemsPerPage * currentPage), totalCount);
  const currentSetMax = Math.min(((itemsPerPage * currentPage) + itemsPerPage), totalCount);
  return `${currentSetMin} - ${currentSetMax} of ${totalCount}`;
};
